import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Container, Heading, Input, Jumbo } from '@treams/treams-ui';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';

import GoalsList from './GoalsList';
import GoalMoments from './GoalMoments';
import GoalsTabs from './GoalsTabs';
import NewGoalButton from './NewGoalButton';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import { Flex, Box } from 'reflexbox';
import { rgba } from 'polished';
import { MagicWandIcon, SendIcon } from '@treams/treams-icons';
import { useHistory } from '@treams/treams-routes';

const Background = styled(Container)`
	background: ${(props) => props.theme.colors.whiteBlue};
`;

export function PersonalGoalsList(props: {
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	hideArchived?: boolean;
}) {
	const viewer = useAuthenticatedUser();

	const query = graphql.useIndividualGoalsQuery({
		variables: { id: viewer.id, status: graphql.GoalStatus.Active, last: 10 },
		fetchPolicy: 'network-only',
	});
	const completedQuery = graphql.useIndividualGoalsQuery({
		variables: { id: viewer.id, status: graphql.GoalStatus.Completed, last: 10 },
		fetchPolicy: 'network-only',
	});

	const archivedQuery = graphql.useIndividualGoalsLazyQuery({
		variables: { id: viewer.id, status: graphql.GoalStatus.Archived, last: 10 },
		fetchPolicy: 'network-only',
	});

	return (
		<GoalsList
			moments={<GoalMoments userID={viewer.id} />}
			query={query}
			path="user.goalsV2"
			completedQuery={completedQuery}
			archivedQuery={!props.hideArchived ? archivedQuery : undefined}
			selectedGoal={props.selectedGoal}
			onSelect={props.onSelect}
			reorderable
		/>
	);
}

const GoalMagicShortcutCard = styled(Box)`
	border-radius: 10px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.1)},
		0px 1px 2px ${(props) => rgba(props.theme.colors.slateBlue, 0.05)};
	border: 2px solid ${(props) => props.theme.colors.purple};
	gap: 20px;
`;

const GoalMagicShortcutVisual = styled(Box)`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: ${(props) => props.theme.colors.purple};
	padding: 8px;

	> svg path {
		fill: white;
	}
`;

const GoalMagicShortcutLabel = styled(Box)`
	font-size: 16px;
	color: ${(props) => props.theme.colors.slateBlue};
`;

function GoalMagicShortcut() {
	const { t } = useTranslation();
	const [input, setInput] = useState('');
	const { push } = useHistory();

	const placeholders = t('goals.magic.shortcut.placeholders')
		.split(', ')
		.map((text) => text + '...' + ' '.repeat(20));

	const [placeholderIdx, setPlaceholderIdx] = useState(0);
	const [placeholderLength, setPlaceholderLength] = useState(0);

	const placeholder = placeholders[placeholderIdx].slice(0, placeholderLength);
	const animatePlaceholder = input === '';

	useEffect(() => {
		if (animatePlaceholder) {
			const interval = setInterval(() => {
				setPlaceholderLength((length) => {
					if (length < placeholders[placeholderIdx].length) {
						return length + 1;
					} else {
						setPlaceholderIdx((idx) => (idx + 1) % placeholders.length);
						return 0;
					}
				});
			}, 50);

			return () => clearInterval(interval);
		} else {
			setPlaceholderLength(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [animatePlaceholder]);

	const startGoalMagic = useCallback(() => {
		const prompt = input.trim();
		push(`/goals/new/?magic_prompt=${encodeURIComponent(prompt)}`);
	}, [input, push]);

	return (
		<GoalMagicShortcutCard mb={5} p="24px" pr={4} pb={4}>
			<Flex mb={3} alignItems="center">
				<GoalMagicShortcutVisual mr={3}>
					<MagicWandIcon />
				</GoalMagicShortcutVisual>
				<Box>
					<GoalMagicShortcutLabel mb={2}>{t('goals.magic.shortcut.title')}</GoalMagicShortcutLabel>
					<Heading>{t('goals.magic.shortcut.trigger')}</Heading>
				</Box>
			</Flex>
			<Flex alignItems="center" ml={3}>
				<Input
					value={input}
					onChange={setInput}
					flexGrow={1}
					mr={2}
					ml="40px"
					placeholder={placeholder}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							startGoalMagic();
							event.preventDefault();
						}
					}}
				/>
				<Button
					title={t('common.start')}
					icon={<SendIcon />}
					type="transparent"
					onClick={startGoalMagic}
					flexShrink={0}
					height={40}
				/>
			</Flex>
		</GoalMagicShortcutCard>
	);
}

export default function ViewerGoals() {
	const { t } = useTranslation();
	const viewer = useViewer();

	const hasGoalsAI = useFeature(graphql.FeatureName.GoalsAi);

	return (
		<>
			<Jumbo
				title={t('goals.index.title')}
				description={t('goals.index.description')}
				button={viewer?.id && <NewGoalButton userID={viewer.id} />}
				footer={<GoalsTabs />}
			/>
			<Background py={4} flexGrow={1} flexShrink={0}>
				{hasGoalsAI && <GoalMagicShortcut />}
				<PersonalGoalsList />
			</Background>
		</>
	);
}
